/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const Form = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
    website: '', // honeypot — hidden from real users, never shown in UI
  });
  const [formErrors, setFormErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    message: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState({
    error: false,
    message: '',
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: false }));
  };

  const validateForm = () => {
    const newErrors = { firstName: false, lastName: false, email: false, message: false };
    let hasErrors = false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (formData.firstName.trim().length < 2) { newErrors.firstName = true; hasErrors = true; }
    else newErrors.firstName = false;

    if (formData.lastName.trim().length < 2) { newErrors.lastName = true; hasErrors = true; }
    else newErrors.lastName = false;

    if (!emailRegex.test(formData.email.trim())) { newErrors.email = true; hasErrors = true; }
    else newErrors.email = false;

    if (formData.message.trim().length < 10) { newErrors.message = true; hasErrors = true; }
    else newErrors.message = false;

    setFormErrors(newErrors);
    return hasErrors;
  };

  const handleSubmit = async () => {
    if (validateForm()) return;

    setIsLoading(true);
    setSubmitError({ error: false, message: '' });

    try {
      const response = await fetch(`${API_URL}/contact/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSuccess(true);
      } else if (response.status === 429) {
        setSubmitError({
          error: true,
          message: 'Too many submissions. Please wait a while before trying again.',
        });
      } else {
        const data = await response.json().catch(() => ({}));
        const errorMessage = data?.errors?.join(' ') || data?.error || 'Something went wrong. Please try again.';
        setSubmitError({
          error: true,
          message: errorMessage,
        });
      }
    } catch (err) {
      setSubmitError({
        error: true,
        message: 'Unable to reach the server. Please check your connection and try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearForm = () => {
    setFormData({ firstName: '', lastName: '', email: '', message: '', website: '' });
  };

  // Auto-clear the success message after 45 seconds and reset the form
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => {
      setSuccess(false);
      clearForm();
    }, 45000);
    return () => clearTimeout(timer);
  }, [success]);

  return (
    <Box>
      {/* Honeypot field — visually hidden, never filled by real users */}
      <Box
        component="input"
        name="website"
        value={formData.website}
        onChange={handleChange}
        tabIndex={-1}
        aria-hidden="true"
        sx={{
          position: 'absolute',
          left: '-9999px',
          width: 1,
          height: 1,
          overflow: 'hidden',
          opacity: 0,
          pointerEvents: 'none',
        }}
      />

      <Box component={Card} boxShadow={1} marginBottom={4} padding={{ xs: 3, sm: 6 }} width={1}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <TextField
              color="primary"
              error={formErrors.firstName}
              fullWidth
              label="First name"
              name="firstName"
              onChange={handleChange}
              size="medium"
              sx={{ height: 54 }}
              value={formData.firstName}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              color="primary"
              error={formErrors.lastName}
              fullWidth
              label="Last name"
              name="lastName"
              onChange={handleChange}
              size="medium"
              sx={{ height: 54 }}
              value={formData.lastName}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              color="primary"
              error={formErrors.email}
              fullWidth
              label="Email"
              name="email"
              onChange={handleChange}
              type="email"
              size="medium"
              sx={{ height: 54 }}
              value={formData.email}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              color="primary"
              error={formErrors.message}
              fullWidth
              label="Message"
              multiline
              name="message"
              onChange={handleChange}
              rows={6}
              size="medium"
              value={formData.message}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item container justifyContent="center" xs={12}>
            <Button
              color="primary"
              disabled={success || isLoading}
              fullWidth
              onClick={handleSubmit}
              size="medium"
              sx={{ height: 54, minWidth: 150 }}
              type="submit"
              variant="contained"
            >
              {isLoading ? <CircularProgress color="inherit" size={24} /> : 'Submit'}
            </Button>

            {/* Success */}
            <Collapse in={success} sx={{ width: '100%' }}>
              <Alert
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => { setSuccess(false); clearForm(); }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                sx={{ mt: 2 }}
              >
                <Typography align="left" sx={{ fontSize: '1rem', width: '100%' }}>
                  Thank you for contacting us! We'll get back to you as soon as possible.
                </Typography>
              </Alert>
            </Collapse>

            {/* Error */}
            <Collapse in={submitError.error} sx={{ width: '100%' }}>
              <Alert
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setSubmitError({ error: false, message: '' })}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                severity="error"
                sx={{ mt: 2 }}
              >
                <Typography align="left" sx={{ fontSize: '1rem', width: '100%' }}>
                  {submitError.message}
                  <br /><br />
                  If the problem persists, email us directly at{' '}
                  <a href="mailto:sales@bridgefirstforecasting.com">
                    sales@bridgefirstforecasting.com
                  </a>
                  .
                </Typography>
              </Alert>
            </Collapse>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Form;
